import React, { useMemo, useState } from "react";

// Components

// Interfaces
import { useQuery, useMutation } from "react-query";

import { Fulfillment } from "@secondcloset/types";
import { ActivityHistoryExportModal, ActivityHistoryList, CheckboxDropdown } from "@secondcloset/web-components";

import { Button, DatePicker, Modal, Row } from "antd";
import { isEmpty, orderBy, startCase, uniqueId } from "lodash-es";
import moment, { Moment } from "moment";
import { RangeValue } from "rc-picker/lib/interface";

import * as S from "./styles";

import { fetchOrderLogs } from "../../api/fulfillment/order";
import { exportProductLogs, fetchProductLogs } from "../../api/fulfillment/product";
import { useFacilityIndex } from "../../hooks/api/common/useFacilityIndex";
import { WarehouseLog, fetchWarehouseProductLogs } from "../../api/warehouse/product";
import { pluralize } from "../../lib/pluralize";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 5;

export enum ActivityHistoryType {
  PRODUCT = "product",
  ORDER = "order",
  WAREHOUSE_PRODUCT = "warehouse_product",
}

const PRODUCT_TAB_LIST = [
  { key: "all", tab: "All" },
  { key: "manual", tab: "Manual" },
  { key: "system", tab: "System" },
  { key: "asn", tab: "ASN" },
  { key: "order", tab: "Order" },
  { key: "rma", tab: "RMA" },
  { key: "inventory_channel_transfer", tab: "Channel Stock Reallocation" },
];
const ORDER_TAB_LIST = [
  { key: "all", tab: "All" },
  { key: "manual", tab: "Manual" },
  { key: "system", tab: "System" },
];

const WAREHOUSE_PRODUCT_TAB_LIST = [
  { key: "all", tab: "All" },
  { key: "warehouse.adjustment", tab: "Inventory Adjustments" },
  { key: "warehouse.status.change", tab: "Status Change" },
  { key: "warehouse.pick", tab: "Picked" },
  { key: "warehouse.receive", tab: "Received" },
];

const getDescription = (event: WarehouseLog) => {
  const stock_count = event.data.physical_stock_count_change;
  const quantity = event.data.quantity;
  const unitText = pluralize(quantity, "unit");
  const reason = startCase(event.data.reason_code).toLowerCase();

  switch (event.event_name) {
    case "warehouse.adjustment":
      return `Inventory adjustment for ${stock_count} ${unitText} due to ${reason}`;
    case "warehouse.status.change":
      return `Status change from ${event.data.from_status} to ${event.data.to_status} for ${quantity} ${unitText}`;
    case "warehouse.pick":
      return `Inventory picked for ${stock_count} ${unitText} due to ${event.data.reference_number}`;
    case "warehouse.receive":
      return `Inventory received for ${stock_count} ${unitText} due to ${event.data.reference_number}`;
    default:
      throw new Error("Unsupported event");
  }
};

const getDataFetcher = (type: ActivityHistoryType) => {
  switch (type) {
    case ActivityHistoryType.ORDER:
      return fetchOrderLogs;
    case ActivityHistoryType.PRODUCT:
      return fetchProductLogs;
    case ActivityHistoryType.WAREHOUSE_PRODUCT:
      return fetchWarehouseProductLogs;
    default:
      throw new Error("Unsupported activity history type");
  }
};

const getTabList = (type: ActivityHistoryType) => {
  switch (type) {
    case ActivityHistoryType.ORDER:
      return ORDER_TAB_LIST;
    case ActivityHistoryType.PRODUCT:
      return PRODUCT_TAB_LIST;
    case ActivityHistoryType.WAREHOUSE_PRODUCT:
      return WAREHOUSE_PRODUCT_TAB_LIST;
    default:
      throw new Error("Unsupported activity history type");
  }
};

const dateFormat = "YYYY/MM/DD";
const defaultEndDate = moment();
const defaultStartDate = moment(defaultEndDate).subtract(6, "M").startOf("day");

interface ActivityHistoryProps {
  id: string;
  type: ActivityHistoryType;
  title?: React.ReactNode | string;
  orgID?: string;
  enableFetch: boolean;
  product?: { sku: string; upc?: string };
}

type ActivityEvent = Fulfillment.ActivityEvent;
type History = Fulfillment.ActivityHistory;

const ActivityHistory: React.FC<ActivityHistoryProps> = ({ id, type, title, enableFetch, orgID, product }) => {
  const [event, setEvent] = useState<ActivityEvent>();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filterFacilities, setFilterFacilities] = useState<string[]>([]);
  const [exportFacilities, setExportFacilities] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [dateRange, setDateRange] = useState<RangeValue<Moment>>([
    moment(defaultStartDate, dateFormat),
    moment(defaultEndDate, dateFormat),
  ]);
  const [warehouseLogDate, setWarehouseLogDate] = useState<moment.Moment | null>(moment());

  const query =
    type === ActivityHistoryType.WAREHOUSE_PRODUCT
      ? {
          id,
          product,
          date_from: warehouseLogDate?.startOf("day").toISOString(),
          date_to: warehouseLogDate?.endOf("day").toISOString(),
          organization_id: orgID,
          included_event_names: event
            ? [event]
            : Object.values(WAREHOUSE_PRODUCT_TAB_LIST)
                .filter((i) => i.key !== "all")
                .map((i) => i.key),
        }
      : {
          id,
          event,
          page,
          per_page: pageSize,
          ...(type === ActivityHistoryType.PRODUCT && { facility: filterFacilities }),
        };

  const activityHistory = useQuery(
    [`${type}-activity-history`, query],
    () => getDataFetcher(type)(query) as Promise<History[] | WarehouseLog[]>,
    {
      enabled: enableFetch,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      select(data) {
        if ((data as WarehouseLog[]).every((d) => d?.source_platform === "warehouse")) {
          return orderBy(
            (data as WarehouseLog[]).map((d) => ({
              id: uniqueId(d.event_name),
              description: [getDescription(d)],
              created_at: d.created_at,
              event: d.event_name as ActivityEvent,
              metadata: { facility: { id: d.data.facility, name: d.data.facility } },
            })),
            ["created_at"],
            ["desc"]
          );
        } else return (data as History[]).map((d) => ({ ...d, id: d.id || uniqueId(d.event) }));
      },
    }
  );

  const facilityIndex = useFacilityIndex({
    enabled: !isEmpty(activityHistory.data),
    select: (response) => response.filter((f) => f.name !== "unknown"),
  });

  const { mutateAsync: submitProductLogsExport, ...productLogsExport } = useMutation(exportProductLogs, {
    onMutate: () => setIsVisible(false),
    onSuccess: ({ message }) => {
      Modal.success({
        title: "Product log request submitted",
        content: message,
      });
    },
  });

  const facilityOptions = useMemo(() => {
    return facilityIndex.data?.map((f) => ({
      value: f.name,
      key: f.name,
      label: `${f.name.toUpperCase()} ${f.address.city}`,
    }));
  }, [facilityIndex.data]);

  const getRedirectUrl = (id: string, type: string) => {
    switch (type) {
      case "user":
        return `/organizations/${orgID}/users/${id}/edit`;
      case "asn":
        return `/fulfillment/asns/${id}`;
      case "order":
        return `/fulfillment/orders/${id}`;
      case "shipment":
        return `/fulfillment/orders/${query.id}/shipment/${id}`;
      case "rma":
        return `/organizations/${orgID}/returns/${id}`;
    }
    return "/";
  };

  const handleTabChange = (key: string) => {
    setEvent((key !== "all" ? key : undefined) as ActivityEvent);
    setPage(1);
  };

  const handleSubmitExport = () => {
    const [date_from, date_to] = dateRange as [Moment, Moment];
    if (!date_from || !date_to || !exportFacilities) return;
    submitProductLogsExport({
      productID: id,
      date_from: moment(date_from).format(dateFormat),
      date_to: moment(date_to).format(dateFormat),
      ...(!isEmpty(exportFacilities) && { facility: exportFacilities }),
    });
  };

  const getPagination = () => {
    if (type === ActivityHistoryType.WAREHOUSE_PRODUCT) return;
    return {
      current: page,
      pageSize,
      onChange: (page: number, pageSize?: number) => {
        setPage(page);
        setPageSize(pageSize ?? DEFAULT_PAGE_SIZE);
      },
    };
  };

  const renderTabActionButtons = () => {
    if (type === ActivityHistoryType.WAREHOUSE_PRODUCT)
      return (
        <Row justify="end">
          <DatePicker
            allowClear={false}
            defaultValue={moment(warehouseLogDate, dateFormat)}
            format={dateFormat}
            onChange={setWarehouseLogDate}
          />
        </Row>
      );
    if (type === ActivityHistoryType.PRODUCT)
      return (
        <Row justify="space-between">
          <CheckboxDropdown
            options={facilityOptions ?? []}
            defaultText="Filter by Warehouse"
            selectedOptionKeys={filterFacilities}
            setSelectedOptionKeys={setFilterFacilities}
            loading={facilityIndex.isLoading}
          />
          <Button type="primary" onClick={() => setIsVisible(true)}>
            Export
          </Button>
        </Row>
      );
  };

  const renderExportModal = () => (
    <ActivityHistoryExportModal
      visible={isVisible}
      confirmLoading={productLogsExport.isLoading}
      selectWarehouseProps={{
        options: facilityOptions ?? [],
        selectedOptionKeys: exportFacilities,
        setSelectedOptionKeys: setExportFacilities,
        loading: facilityIndex.isLoading,
        disabled: productLogsExport.isLoading,
      }}
      dateRangeProps={{
        value: dateRange ?? [null, null],
        onCalendarChange: setDateRange,
        format: dateFormat,
        disabled: productLogsExport.isLoading,
      }}
      onOk={handleSubmitExport}
      okButtonProps={{
        disabled: isEmpty(dateRange),
      }}
      onCancel={() => setIsVisible(false)}
    />
  );

  return (
    <S.Container>
      <>{title ?? "ACTIVITY HISTORY"}</>
      <ActivityHistoryList
        isAdmin
        activityHistory={activityHistory.data ?? []}
        loading={activityHistory.isLoading}
        tabList={getTabList(type)}
        activeTabKey={event ?? "all"}
        onTabChange={handleTabChange}
        getRedirectUrl={getRedirectUrl}
        tabBarExtraContent={renderTabActionButtons()}
        pagination={getPagination()}
      />
      {renderExportModal()}
    </S.Container>
  );
};

export default ActivityHistory;
